import { ref } from 'vue';
import { defineStore } from 'pinia';
import { captureEvent } from '../analytics';

// One entry per queryable data type. Mirror of DataTypeAvailability
// (services/data_availability/response_schemas.py). Dates arrive as ISO
// strings over JSON and are formatted client-side via $d.
export interface DataTypeAvailability {
    type_name: string;
    display_name: string;
    // Long, tuned LLM-selection text. Present in the payload but NOT displayed
    // — the panel renders short_display_description instead.
    description: string;
    // Short (~90 char) label for display. "" when the type hasn't declared one.
    short_display_description: string;
    record_count: number;
    earliest_date: string | null;
    latest_date: string | null;
    date_column: string | null;
    derived_from: string | null;
}

// The "Matter Data Inventory" panel: a read-only, per-matter summary of which
// data types are available to query and how much data each holds. Opens as a
// right-hand panel that shrinks the main content (see MatterPage.vue), toggled
// from the app header (App.vue). Fetches fresh on every open so counts are
// current. Follows the dataLoadStore open/close pattern.
export const useDataInventoryStore = defineStore('dataInventory', () => {
    const isOpen = ref(false);
    const matterId = ref<string | null>(null);
    const dataTypes = ref<DataTypeAvailability[]>([]);
    const loading = ref(false);
    const error = ref<string | null>(null);

    async function fetchInventory(id: string): Promise<void> {
        loading.value = true;
        error.value = null;
        try {
            const response = await fetch(`/api/Matter/${id}/DataAvailability`);
            if (!response.ok) {
                // 400 (no availability map yet) and everything else fall through
                // to a plain empty list — the panel renders its empty state.
                dataTypes.value = [];
                return;
            }
            const data = await response.json();
            const types: DataTypeAvailability[] = data.data_types ?? [];
            dataTypes.value = types;

            // Second half of the open event: the resolved type count, once we
            // have it. Counts only — no type names or descriptions.
            captureEvent('data_inventory_opened', {
                matter_id: id,
                type_count: types.length,
            });
        } catch (e: unknown) {
            error.value = e instanceof Error ? e.message : 'Unknown error';
            dataTypes.value = [];
        } finally {
            loading.value = false;
        }
    }

    function open(id: string) {
        matterId.value = id;
        isOpen.value = true;
        // Analytics: entry into the inventory. matter_id up front; type_count
        // follows in fetchInventory once the list resolves.
        captureEvent('data_inventory_opened', { matter_id: id });
        void fetchInventory(id);
    }

    function close() {
        isOpen.value = false;
        // Reset transient view state so the panel opens fresh next time rather
        // than flashing the previous matter's list/error.
        dataTypes.value = [];
        error.value = null;
        loading.value = false;
    }

    function toggle(id: string) {
        if (isOpen.value) {
            close();
        } else {
            open(id);
        }
    }

    return {
        isOpen,
        matterId,
        dataTypes,
        loading,
        error,
        fetchInventory,
        open,
        close,
        toggle,
    };
});
